import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CustomerDialog } from "@/components/CustomerDialog";

interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsapp?: string;
  description?: string;
}

interface CustomerSelectProps {
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
  customerName: string;
  setCustomerName: (name: string) => void;
}

export function CustomerSelect({
  selectedCustomer,
  setSelectedCustomer,
  customerName,
  setCustomerName,
}: CustomerSelectProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchCustomers = async () => {
      if (customerName.trim().length >= 2) {
        setIsSearching(true);
        try {
          const { data, error } = await supabase
            .from('customers')
            .select('*')
            .ilike('name', `%${customerName}%`)
            .limit(5);

          if (error) throw error;

          setCustomers(data || []);
          setShowDropdown(true);

          if (data.length === 0) {
            toast({
              title: "Cliente não encontrado",
              description: "Clique no botão + para cadastrar um novo cliente",
              variant: "default",
            });
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setCustomers([]);
        setShowDropdown(false);
      }
    };

    const debounceTimer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [customerName, toast]);

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setShowDropdown(false);
  };

  const handleNewCustomerSave = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    toast({
      title: "Cliente cadastrado",
      description: "Cliente cadastrado com sucesso!",
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.customer-select-container')) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="space-y-2 customer-select-container">
      <Label>Cliente *</Label>
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input 
              type="text"
              placeholder="Digite o nome do cliente..." 
              value={customerName}
              onChange={(e) => {
                setCustomerName(e.target.value);
                if (!e.target.value) {
                  setSelectedCustomer(null);
                }
              }}
              className="w-full bg-[#1A1F2C] text-white border-gray-700 focus:border-[#0073B7] focus:ring-[#0073B7]"
              onFocus={() => {
                if (customers.length > 0) {
                  setShowDropdown(true);
                }
              }}
            />
            {isSearching && (
              <div className="absolute right-3 top-2.5">
                <Loader2 className="h-5 w-5 animate-spin text-[#0073B7]" />
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="border-gray-700 hover:bg-[#0073B7] hover:text-white"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {showDropdown && customers.length > 0 && (
          <div 
            className="absolute z-50 w-full mt-1 bg-[#1A1F2C] border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="px-4 py-2 cursor-pointer hover:bg-[#0073B7] hover:text-white text-white transition-colors"
                onClick={() => handleCustomerSelect(customer)}
              >
                {customer.name}
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedCustomer && (
        <div className="mt-2 p-3 bg-[#1A1F2C] text-white rounded-md text-sm border border-gray-700">
          <p><strong>Cliente selecionado:</strong> {selectedCustomer.name}</p>
          <p><strong>Telefone:</strong> {selectedCustomer.phone}</p>
          {selectedCustomer.whatsapp && (
            <p><strong>WhatsApp:</strong> {selectedCustomer.whatsapp}</p>
          )}
        </div>
      )}

      <CustomerDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSave={handleNewCustomerSave}
      />
    </div>
  );
}